import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

const ApplicationForm = ({ fund, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    organization_name: '',
    organization_type: '',
    sector: '',
    funding_amount_requested: '',
    funding_purpose: '',
    pitch_description: '',
    impact_description: '',
    beneficiaries_reached: '',
    pitch_deck_url: '',
    financials_url: '',
    impact_report_url: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateStep1 = () => {
    if (!formData.organization_name || !formData.organization_type || !formData.sector) {
      setError('Please fill in all required fields');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.funding_amount_requested || !formData.funding_purpose || !formData.pitch_description) {
      setError('Please fill in all required fields');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    setError('');
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleBack = () => {
    setError('');
    setStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const applicationData = {
        fund_id: fund.id,
        ...formData,
        funding_amount_requested: parseInt(formData.funding_amount_requested),
        beneficiaries_reached: formData.beneficiaries_reached ? parseInt(formData.beneficiaries_reached) : null
      };

      await axios.post(
        `${API_BASE_URL}/api/funding-options/applications`,
        applicationData,
        { withCredentials: true }
      );

      onSuccess();
    } catch (error) {
      console.error('Error submitting application:', error);
      setError(error.response?.data?.detail || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <Card className="w-full max-w-3xl bg-white my-8">
        <div className="p-6 border-b">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-2xl font-bold">Apply for Funding</h2>
              <p className="text-gray-600 mt-1">{fund.fund_name}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-[#E87722] text-white' : 'bg-gray-200'}`}>
                1
              </div>
              <span className={`ml-2 text-sm ${step >= 1 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                Organization
              </span>
            </div>
            <div className="flex-1 h-0.5 bg-gray-200 mx-4">
              <div className={`h-full ${step >= 2 ? 'bg-[#E87722]' : 'bg-gray-200'}`} style={{ width: step >= 2 ? '100%' : '0%' }}></div>
            </div>
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-[#E87722] text-white' : 'bg-gray-200'}`}>
                2
              </div>
              <span className={`ml-2 text-sm ${step >= 2 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                Funding Details
              </span>
            </div>
            <div className="flex-1 h-0.5 bg-gray-200 mx-4">
              <div className={`h-full ${step >= 3 ? 'bg-[#E87722]' : 'bg-gray-200'}`} style={{ width: step >= 3 ? '100%' : '0%' }}></div>
            </div>
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-[#E87722] text-white' : 'bg-gray-200'}`}>
                3
              </div>
              <span className={`ml-2 text-sm ${step >= 3 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                Impact & Documents
              </span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Step 1: Organization Details */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Organization Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="organization_name"
                  value={formData.organization_name}
                  onChange={handleInputChange}
                  className="w-full border rounded-md p-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Organization Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="organization_type"
                  value={formData.organization_type}
                  onChange={handleInputChange}
                  className="w-full border rounded-md p-2"
                  required
                >
                  <option value="">Select type</option>
                  <option value="MSME">MSME</option>
                  <option value="NGO">NGO</option>
                  <option value="Social Enterprise">Social Enterprise</option>
                  <option value="Startup">Startup</option>
                  <option value="Cooperative">Cooperative</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Primary Sector <span className="text-red-500">*</span>
                </label>
                <select
                  name="sector"
                  value={formData.sector}
                  onChange={handleInputChange}
                  className="w-full border rounded-md p-2"
                  required
                >
                  <option value="">Select sector</option>
                  <option value="Financial Inclusion">Financial Inclusion</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Education">Education</option>
                  <option value="Agriculture">Agriculture</option>
                  <option value="Climate">Climate</option>
                  <option value="Technology">Technology</option>
                  <option value="Livelihoods">Livelihoods</option>
                  <option value="Rural Development">Rural Development</option>
                  <option value="Clean Energy">Clean Energy</option>
                  <option value="Sustainability">Sustainability</option>
                </select>
              </div>
            </div>
          )}

          {/* Step 2: Funding Details */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Funding Amount Requested (₹ Lakhs) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="funding_amount_requested"
                  value={formData.funding_amount_requested}
                  onChange={handleInputChange}
                  className="w-full border rounded-md p-2"
                  placeholder="e.g., 50"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Fund ticket size: {fund.ticket_size_min && fund.ticket_size_max 
                    ? `₹${fund.ticket_size_min}L - ₹${fund.ticket_size_max}L`
                    : 'Not specified'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Funding Purpose <span className="text-red-500">*</span>
                </label>
                <select
                  name="funding_purpose"
                  value={formData.funding_purpose}
                  onChange={handleInputChange}
                  className="w-full border rounded-md p-2"
                  required
                >
                  <option value="">Select purpose</option>
                  <option value="Expansion">Business Expansion</option>
                  <option value="Working Capital">Working Capital</option>
                  <option value="Technology">Technology Development</option>
                  <option value="Market Entry">Market Entry</option>
                  <option value="Product Development">Product Development</option>
                  <option value="Infrastructure">Infrastructure</option>
                  <option value="Team Building">Team Building</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Pitch Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="pitch_description"
                  value={formData.pitch_description}
                  onChange={handleInputChange}
                  rows="6"
                  className="w-full border rounded-md p-2"
                  placeholder="Describe your organization, problem you're solving, solution, business model, and growth plans..."
                  required
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">
                  Minimum 200 characters. Be clear and concise.
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Impact & Documents */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Impact Description
                </label>
                <textarea
                  name="impact_description"
                  value={formData.impact_description}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full border rounded-md p-2"
                  placeholder="Describe your social/environmental impact, metrics, and achievements..."
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Beneficiaries Reached (if applicable)
                </label>
                <input
                  type="number"
                  name="beneficiaries_reached"
                  value={formData.beneficiaries_reached}
                  onChange={handleInputChange}
                  className="w-full border rounded-md p-2"
                  placeholder="e.g., 10000"
                />
              </div>

              <div className="border-t pt-4">
                <h3 className="font-medium mb-3">Supporting Documents (Optional)</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Upload your documents to Google Drive or Dropbox and paste the sharing links below
                </p>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Pitch Deck URL
                    </label>
                    <input
                      type="url"
                      name="pitch_deck_url"
                      value={formData.pitch_deck_url}
                      onChange={handleInputChange}
                      className="w-full border rounded-md p-2"
                      placeholder="https://drive.google.com/..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Financials URL
                    </label>
                    <input
                      type="url"
                      name="financials_url"
                      value={formData.financials_url}
                      onChange={handleInputChange}
                      className="w-full border rounded-md p-2"
                      placeholder="https://drive.google.com/..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Impact Report URL
                    </label>
                    <input
                      type="url"
                      name="impact_report_url"
                      value={formData.impact_report_url}
                      onChange={handleInputChange}
                      className="w-full border rounded-md p-2"
                      placeholder="https://drive.google.com/..."
                    />
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-md">
                <p className="text-sm text-gray-700">
                  <strong>Next Steps:</strong> After submission, CSED will review your application and provide feedback. 
                  If approved, we'll forward it to {fund.fund_name} on your behalf.
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between mt-6 pt-6 border-t">
            <div>
              {step > 1 && (
                <Button
                  type="button"
                  onClick={handleBack}
                  variant="outline"
                  disabled={loading}
                >
                  Back
                </Button>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                disabled={loading}
              >
                Cancel
              </Button>
              {step < 3 ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  className="bg-[#E87722] hover:bg-[#d16b1a]"
                >
                  Next
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[#E87722] hover:bg-[#d16b1a]"
                >
                  {loading ? 'Submitting...' : 'Submit Application'}
                </Button>
              )}
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ApplicationForm;
